import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { wsPipeline } from "@/lib/workspace";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import { daysBetween, deriveInitials, isNextActionDue, isOverdueInStage } from "@/lib/pipeline";
import { stageColor } from "@/lib/palette";
import { Sun, Clock, Ban, AlertTriangle, ListChecks, AtSign, Zap, Building2 } from "lucide-react";
import QuickAddDialog from "@/components/QuickAddDialog";
import { canWrite } from "@/lib/roles";
import { PASTEL } from "@/lib/pastel";
import CoachCard from "@/components/CoachCard";
import { useNavigate } from "react-router-dom";

const OUT_STATUS = {
  overdue: { label: "Overdue", cls: "bg-[#b91c1c]/10 text-[#b91c1c] border-[#b91c1c]/30" },
  due_today: { label: "Due today", cls: "bg-[#FBBF24]/20 text-[#7a5a1a] border-[#FBBF24]/50" },
  due_soon: { label: "Due soon", cls: "bg-[#FFF9C4] text-[#7a5a1a] border-[#FFCB05]/40" },
  none: { label: "No date", cls: "bg-[#15233B]/5 text-[#15233B]/50 border-[#15233B]/10" },
};
const OUT_DUE = new Set(["overdue", "due_today", "due_soon"]);

export default function MyDayPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState({});
  const [selected, setSelected] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [outreach, setOutreach] = useState([]);

  const reload = () =>
    Promise.all([
      api.get("/cards", { params: { pipeline: wsPipeline() } }),
      api.get("/templates", { params: { pipeline: wsPipeline() } }),
      api.get("/users"),
      api.get("/lists"),
      api.get("/mentions/me", { params: { limit: 8 } }),
      api.get("/outreach", { params: { mine: true } }),
    ]).then(([c, t, u, l, m, o]) => {
      setAllCards(c.data);
      setCards(c.data.filter((x) => x.owner_id === user.id));
      setTemplates(t.data);
      setUsers(u.data);
      setLists(l.data);
      setMentions(m.data);
      setOutreach((o.data.items || []).filter((p) => OUT_DUE.has(p.followup_status)));
    });

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const tplByStage = useMemo(() => Object.fromEntries(templates.map((t) => [t.stage_number, t])), [templates]);

  const today = new Date().toISOString().slice(0, 10);
  const due = cards.filter((c) => c.next_action_date && c.next_action_date <= today);
  const overdue = cards.filter((c) => isOverdueInStage(c, tplByStage[c.current_stage]));
  const blocked = cards.filter((c) => c.blocked);
  // Tasks assigned to me across ALL active cards (not just my owned cards)
  const [allCards, setAllCards] = useState([]);
  const [mentions, setMentions] = useState([]);
  const myTasks = [];
  allCards.forEach((c) => {
    (c.tasks_state || []).forEach((t, idx) => {
      if (t.assigned_to === user.id && !t.done) {
        myTasks.push({ ...t, idx, card: c });
      }
    });
  });

  const openCard = async (cardId) => {
    try {
      const { data } = await api.get(`/cards/${cardId}`);
      setSelected(data);
    } catch {
      /* noop */
    }
  };
  const onCardChanged = (updated) => {
    setSelected(updated);
    setAllCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setCards((prev) => {
      const exists = prev.find((c) => c.id === updated.id);
      if (exists) return prev.map((c) => (c.id === updated.id ? updated : c));
      // if owner switched to me, add it; if away from me, remove it
      if (updated.owner_id === user.id) return [...prev, updated];
      return prev;
    });
  };

  const Section = ({ title, icon: Icon, items, pastel, render, empty, testId }) => (
    <section data-testid={testId} className="bg-white rounded-lg shadow-sm overflow-hidden border" style={{ borderColor: pastel.border }}>
      <div className="px-5 py-3 flex items-center gap-2 border-b" style={{ background: pastel.tint, borderColor: pastel.border, color: pastel.accent }}>
        <Icon className="h-4 w-4" />
        <h3 className="text-sm font-extrabold tracking-tight uppercase tracking-[0.15em]">{title}</h3>
        <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/70" style={{ color: pastel.accent }}>{items.length}</span>
      </div>
      <ul className="divide-y divide-[#15233B]/8">
        {items.length === 0 && <li className="px-5 py-6 text-xs italic text-[#15233B]/40">{empty}</li>}
        {items.map((c) => {
          const sc = stageColor(c.current_stage);
          return (
          <li
            key={c.id}
            onClick={() => openCard(c.id)}
            data-testid={`myday-row-${c.av_id}`}
            className="px-5 py-3 text-sm flex items-center gap-3 cursor-pointer hover:bg-[#F6F3EC]/60 transition-colors"
          >
            <div
              className="h-9 w-9 rounded-full grid place-items-center text-xs font-bold shadow-sm"
              style={{ backgroundColor: sc.primary, color: sc.text }}
            >
              {deriveInitials(c.full_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#15233B] truncate">
                {c.full_name} · <span className="font-mono text-xs">{c.av_id}</span>
              </div>
              <div className="text-xs text-[#15233B]/60 truncate">
                Stage {c.current_stage}: {tplByStage[c.current_stage]?.name}
              </div>
            </div>
            {render(c)}
          </li>
          );
        })}
      </ul>
    </section>
  );

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl w-full mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <Sun className="h-6 w-6 text-[#FBBF24]" />
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">My Day</h1>
          {canWrite(user) && (
            <button
              data-testid="myday-quick-add"
              onClick={() => setShowQuickAdd(true)}
              className="ml-auto px-3 py-2 rounded-md bg-[#15233B] hover:bg-[#1E73C9] text-white text-xs font-extrabold flex items-center gap-1 shadow-sm"
            >
              <Zap className="h-3.5 w-3.5 text-[#FFCB05]" /> Quick add lead
            </button>
          )}
        </div>
        <p className="text-sm text-[#15233B]/60 -mt-2">
          Focus list of your cards. Initials and IDs only.
        </p>

        <CoachCard onOpenCard={(id) => openCard(id)} onNavigate={(r) => navigate(r)} />

        <Section
          title="Next action due today/overdue"
          icon={Clock}
          items={due}
          pastel={PASTEL.yellow}
          empty="Nothing due."
          testId="my-day-due-section"
          render={(c) => (
            <div className="text-xs text-[#15233B]/70 text-right">
              <div className="font-semibold">{c.next_action_date}</div>
              <div className="italic max-w-[200px] truncate">{c.next_action_text || "—"}</div>
            </div>
          )}
        />

        <section data-testid="my-day-outreach-section" className="bg-white rounded-lg shadow-sm overflow-hidden border" style={{ borderColor: "rgba(30,115,201,0.25)" }}>
          <div className="px-5 py-3 flex items-center gap-2 border-b" style={{ background: "rgba(30,115,201,0.08)", borderColor: "rgba(30,115,201,0.2)", color: "#1E73C9" }}>
            <Building2 className="h-4 w-4" />
            <h3 className="text-sm font-extrabold tracking-tight uppercase tracking-[0.15em]">Partner follow-ups due</h3>
            <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/70" style={{ color: "#1E73C9" }}>{outreach.length}</span>
          </div>
          <ul className="divide-y divide-[#15233B]/8">
            {outreach.length === 0 && <li className="px-5 py-6 text-xs italic text-[#15233B]/40">No partner follow-ups due — nice work.</li>}
            {outreach.map((p) => {
              const meta = OUT_STATUS[p.followup_status] || OUT_STATUS.none;
              return (
                <li
                  key={p.id}
                  onClick={() => navigate("/outreach")}
                  data-testid={`myday-outreach-${p.id}`}
                  className="px-5 py-3 text-sm flex items-center gap-3 cursor-pointer hover:bg-[#F6F3EC]/60 transition-colors"
                >
                  <div className="h-9 w-9 rounded-full grid place-items-center shrink-0 bg-[#1E73C9]/10 text-[#1E73C9]">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#15233B] truncate">{p.name}</div>
                    <div className="text-xs text-[#15233B]/60 truncate">{p.category}{p.next_followup_note ? ` · ${p.next_followup_note}` : ""}</div>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${meta.cls}`}>
                    {meta.label}{p.next_followup_date ? ` · ${new Date(p.next_followup_date + "T00:00:00").toLocaleDateString()}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Tasks assigned to me */}
        <section data-testid="my-day-tasks-section" className="bg-white rounded-lg shadow-sm overflow-hidden border" style={{ borderColor: PASTEL.green.border }}>
          <div className="px-5 py-3 flex items-center gap-2 border-b" style={{ background: PASTEL.green.tint, borderColor: PASTEL.green.border, color: PASTEL.green.accent }}>
            <ListChecks className="h-4 w-4" />
            <h3 className="text-sm font-extrabold tracking-tight uppercase tracking-[0.15em]">Tasks assigned to me</h3>
            <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/70" style={{ color: PASTEL.green.accent }}>{myTasks.length}</span>
          </div>
          <ul className="divide-y divide-[#15233B]/8">
            {myTasks.length === 0 && <li className="px-5 py-6 text-xs italic text-[#15233B]/40">No tasks assigned to you.</li>}
            {myTasks.map((mt) => {
              const sc = stageColor(mt.card.current_stage);
              const taskOverdue = mt.due_date && mt.due_date <= today;
              return (
                <li
                  key={`${mt.card.id}-${mt.idx}`}
                  onClick={() => openCard(mt.card.id)}
                  data-testid={`myday-task-${mt.card.av_id}-${mt.idx}`}
                  className="px-5 py-3 text-sm flex items-center gap-3 cursor-pointer hover:bg-[#F6F3EC]/60 transition-colors"
                >
                  <div className="h-9 w-9 rounded-full grid place-items-center text-xs font-bold shadow-sm shrink-0" style={{ backgroundColor: sc.primary, color: sc.text }}>
                    {deriveInitials(mt.card.full_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#15233B] truncate">{mt.title}</div>
                    <div className="text-xs text-[#15233B]/60 truncate">
                      {mt.card.full_name} · <span className="font-mono">{mt.card.av_id}</span> · Stage {mt.card.current_stage}
                    </div>
                  </div>
                  {mt.due_date && (
                    <div className={`text-xs font-bold px-2 py-0.5 rounded ${taskOverdue ? "bg-[#b91c1c]/10 text-[#b91c1c]" : "bg-[#F6F3EC] text-[#15233B]"}`}>
                      {mt.due_date}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section data-testid="my-day-mentions-section" className="bg-white rounded-lg shadow-sm overflow-hidden border" style={{ borderColor: PASTEL.violet.border }}>
          <div className="px-5 py-3 flex items-center gap-2 border-b" style={{ background: PASTEL.violet.tint, borderColor: PASTEL.violet.border, color: PASTEL.violet.accent }}>
            <AtSign className="h-4 w-4" />
            <h3 className="text-sm font-extrabold tracking-tight uppercase tracking-[0.15em]">Recent mentions</h3>
            <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/70" style={{ color: PASTEL.violet.accent }}>{mentions.length}</span>
          </div>
          <ul className="divide-y divide-[#15233B]/8">
            {mentions.length === 0 && <li className="px-5 py-6 text-xs italic text-[#15233B]/40">No mentions yet. Teammates can tag you with @firstname in card notes.</li>}
            {mentions.map((m) => (
              <li
                key={m.id}
                onClick={() => openCard(m.card_id)}
                data-testid={`myday-mention-${m.id}`}
                className={`px-5 py-3 text-sm cursor-pointer hover:bg-[#F6F3EC]/60 transition-colors ${!m.read ? "bg-[#EDE9FE]/40" : ""}`}
              >
                <div className="font-bold text-[#15233B]">
                  {m.by_user_name} <span className="font-normal text-[#15233B]/60">on</span> {m.card_full_name} <span className="font-mono text-xs text-[#15233B]/60">{m.card_av_id}</span>
                </div>
                <div className="text-xs italic text-[#15233B]/70 line-clamp-2">“{m.text}”</div>
              </li>
            ))}
          </ul>
        </section>

        <Section
          title="Overdue in stage"
          icon={AlertTriangle}
          items={overdue}
          pastel={PASTEL.blue}
          empty="No overdue cards."
          testId="my-day-overdue-section"
          render={(c) => (
            <div className="text-xs font-bold" style={{ color: PASTEL.blue.accent }}>
              {Math.floor(daysBetween(c.stage_entered_at))}d / {tplByStage[c.current_stage]?.day_target}d
            </div>
          )}
        />

        <Section
          title="Blocked"
          icon={Ban}
          items={blocked}
          pastel={PASTEL.pink}
          empty="No blocked cards."
          testId="my-day-blocked-section"
          render={(c) => (
            <div className="text-xs italic max-w-[220px] truncate" style={{ color: PASTEL.pink.accent }}>
              {c.blocked_reason}
            </div>
          )}
        />
      </div>

      {selected && (
        <CardDetailPanel
          card={selected}
          template={tplByStage[selected.current_stage]}
          templates={templates}
          users={users}
          lists={lists}
          onClose={() => setSelected(null)}
          onChanged={onCardChanged}
        />
      )}
      {showQuickAdd && (
        <QuickAddDialog onClose={() => setShowQuickAdd(false)} onCreated={() => {}} />
      )}
      <BrandFooter />
    </div>
  );
}
